/**
 * Generic CSV/Excel import shell: pick file → optional column map → dry-run → commit.
 * Domain pages supply API callbacks and labels. Column mapping is opt-in via columnFields.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  ConfirmDialog,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@/shared/ui';
import {
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { downloadBlob } from './downloadCsv';
import ImportColumnMapTable from './ImportColumnMapTable';
import {
  compactColumnMapping,
  isColumnMappingComplete,
  suggestColumnMapping,
  type ImportColumnField,
  type ImportInspectResult,
} from './suggestColumnMapping';

export type { ImportColumnField, ImportInspectResult };

export interface ImportCsvErrorRow {
  row: number;
  code?: string;
  message: string;
  errorCode?: string;
  params?: Record<string, string | number>;
}

export interface ImportCsvPreviewResult {
  dryRun: boolean;
  total: number;
  toCreate: number;
  toUpdate: number;
  created?: number;
  updated?: number;
  errors: ImportCsvErrorRow[];
}

export type ImportCsvProgressHandler = (percent: number) => void;

interface ImportCsvDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  templateHint?: React.ReactNode;
  onDownloadTemplate?: () => void | Promise<void>;
  /** When set with onInspect, pick-file opens a column-map step before dry-run. */
  columnFields?: ImportColumnField[];
  onInspect?: (
    file: File,
    onProgress?: ImportCsvProgressHandler
  ) => Promise<ImportInspectResult>;
  onDryRun: (
    file: File,
    onProgress?: ImportCsvProgressHandler,
    columnMapping?: Record<string, string>
  ) => Promise<ImportCsvPreviewResult>;
  onCommit: (
    file: File,
    onProgress?: ImportCsvProgressHandler,
    columnMapping?: Record<string, string>,
    options?: { skipInvalidRows?: boolean }
  ) => Promise<ImportCsvPreviewResult>;
  /** After a check that found row errors, offer to import the valid rows only. */
  allowSkipInvalidRows?: boolean;
  formatRowError?: (error: ImportCsvErrorRow) => string;
  labels: {
    chooseFile: string;
    /** Shown inside the empty drop zone. Falls back to chooseFile. */
    dropHint?: string;
    /** Supported format line under the drop hint, e.g. "CSV hoặc Excel, tối đa 5MB". */
    acceptHint?: string;
    invalidFile?: string;
    changeFile: string;
    commit: string;
    cancel: string;
    close: string;
    downloadTemplate: string;
    downloadErrors?: string;
    errorsCsvFilename?: string;
    noFile: string;
    previewTitle: string;
    total: string;
    toCreate: string;
    toUpdate: string;
    errorsTitle: string;
    row: string;
    code: string;
    message: string;
    readyToCommit: string;
    hasErrors: string;
    resultTitle: string;
    created: string;
    updated: string;
    uploading?: string;
    checking: string;
    checkingWithCount?: string;
    importing: string;
    importingWithCount?: string;
    confirmUpdateTitle?: string;
    confirmUpdateMessage?: string;
    confirmUpdateConfirm?: string;
    skipInvalidRows?: string;
    skipSummary?: string;
    partialResult?: string;
    confirmSkipTitle?: string;
    confirmSkipMessage?: string;
    confirmSkipConfirm?: string;
    check?: string;
    mappingTitle?: string;
    mappingHint?: string;
    mappingField?: string;
    mappingSource?: string;
    mappingSample?: string;
    mappingSkip?: string;
    mappingSubjectHint?: string;
    mappingIncomplete?: string;
    inspecting?: string;
  };
}

type Phase =
  | 'idle'
  | 'inspecting'
  | 'mapping'
  | 'previewing'
  | 'ready'
  | 'importing'
  | 'done';

const IMPORT_ACCEPT =
  '.csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fill = (template: string, vars: Record<string, string | number>): string =>
  template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key: string) =>
    vars[key] != null ? String(vars[key]) : ''
  );

const csvEscape = (value: unknown): string => {
  const raw = value == null ? '' : String(value);
  if (/[",\n\r]/.test(raw)) return `"${raw.replace(/"/g, '""')}"`;
  return raw;
};

const isImportFile = (candidate: File): boolean => {
  const name = candidate.name.toLowerCase();
  return (
    name.endsWith('.csv') ||
    name.endsWith('.xlsx') ||
    name.endsWith('.xls') ||
    candidate.type === 'text/csv' ||
    candidate.type === 'text/plain' ||
    candidate.type === 'application/vnd.ms-excel' ||
    candidate.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  );
};

const estimateCsvRowCount = async (target: File): Promise<number | undefined> => {
  const name = target.name.toLowerCase();
  if (!name.endsWith('.csv') && target.type !== 'text/csv' && target.type !== 'text/plain') {
    return undefined;
  }
  try {
    const text = await target.text();
    const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0);
    return Math.max(0, lines.length - 1);
  } catch {
    return undefined;
  }
};

const getErrorMessage = (err: unknown, fallback: string): string => {
  if (err instanceof Error && err.message) return err.message;
  if (
    typeof err === 'object' &&
    err &&
    'response' in err &&
    typeof (err as { response?: { data?: { message?: string } } }).response?.data
      ?.message === 'string'
  ) {
    return String(
      (err as { response?: { data?: { message?: string } } }).response?.data?.message
    );
  }
  return fallback;
};

const ImportCsvDialog: React.FC<ImportCsvDialogProps> = ({
  open,
  onClose,
  title,
  templateHint,
  onDownloadTemplate,
  columnFields,
  onInspect,
  onDryRun,
  onCommit,
  allowSkipInvalidRows = false,
  formatRowError,
  labels,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportCsvPreviewResult | null>(null);
  const [result, setResult] = useState<ImportCsvPreviewResult | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [rowEstimate, setRowEstimate] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [skipInvalidRows, setSkipInvalidRows] = useState(false);
  const [inspect, setInspect] = useState<ImportInspectResult | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const requestIdRef = useRef(0);

  const mappingEnabled = Boolean(columnFields?.length && onInspect);
  const mappingPayload = mappingEnabled ? compactColumnMapping(mapping) : undefined;
  const mappingComplete =
    !mappingEnabled || isColumnMappingComplete(columnFields || [], mapping);
  const subjectFields = (columnFields || []).filter((field) => field.required === 'subject');
  const subjectMapped = subjectFields.some((field) => mapping[field.key]);

  const busy = phase === 'inspecting' || phase === 'previewing' || phase === 'importing';
  const resolveRowError = (item: ImportCsvErrorRow): string =>
    formatRowError ? formatRowError(item) : item.message;

  const clearTableState = (): void => {
    setPreview(null);
    setResult(null);
    setInspect(null);
    setMapping({});
    setRowEstimate(null);
    setConfirmOpen(false);
    setSkipInvalidRows(false);
  };

  const reset = (): void => {
    requestIdRef.current += 1;
    setFile(null);
    clearTableState();
    setPhase('idle');
    setProgress(0);
    setError(null);
    setIsDragging(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    if (open) return;
    requestIdRef.current += 1;
    setFile(null);
    setPreview(null);
    setResult(null);
    setInspect(null);
    setMapping({});
    setPhase('idle');
    setProgress(0);
    setError(null);
    setIsDragging(false);
    setRowEstimate(null);
    setConfirmOpen(false);
    setSkipInvalidRows(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [open]);

  const handleClose = (): void => {
    if (busy) return;
    reset();
    onClose();
  };

  const runInspect = async (target: File): Promise<void> => {
    if (!onInspect || !columnFields) return;
    const requestId = ++requestIdRef.current;
    setPhase('inspecting');
    setProgress(0);
    setError(null);
    setPreview(null);
    setResult(null);
    setInspect(null);
    setMapping({});
    try {
      const next = await onInspect(target, (percent) => {
        if (requestId === requestIdRef.current) {
          setProgress(percent >= 100 ? 0 : percent);
        }
      });
      if (requestId !== requestIdRef.current) return;
      setInspect(next);
      setMapping(suggestColumnMapping(next.headers, columnFields));
      setPhase('mapping');
      setProgress(0);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(getErrorMessage(err, 'Import failed'));
      setPhase('idle');
      setProgress(0);
    }
  };

  const runDryRun = async (
    target: File,
    mappingOverride?: Record<string, string>
  ): Promise<void> => {
    const requestId = ++requestIdRef.current;
    setPhase('previewing');
    setProgress(0);
    setError(null);
    setPreview(null);
    setResult(null);
    setSkipInvalidRows(false);
    const payload = mappingEnabled
      ? compactColumnMapping(mappingOverride ?? mapping)
      : undefined;
    try {
      const next = await onDryRun(target, (percent) => {
        if (requestId === requestIdRef.current) {
          setProgress(percent >= 100 ? 0 : percent);
        }
      }, payload);
      if (requestId !== requestIdRef.current) return;
      setPreview(next);
      setPhase('ready');
      setProgress(0);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(getErrorMessage(err, 'Import failed'));
      setPhase(mappingEnabled ? 'mapping' : 'idle');
      setProgress(0);
    }
  };

  const runCommit = async (): Promise<void> => {
    if (!file) {
      setError(labels.noFile);
      return;
    }
    const requestId = ++requestIdRef.current;
    setPhase('importing');
    setProgress(0);
    setError(null);
    try {
      const next = await onCommit(file, (percent) => {
        if (requestId === requestIdRef.current) {
          setProgress(percent >= 100 ? 0 : percent);
        }
      }, mappingPayload, {
        skipInvalidRows: allowSkipInvalidRows && skipInvalidRows,
      });
      if (requestId !== requestIdRef.current) return;
      setResult(next);
      setPhase('done');
      setProgress(0);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(getErrorMessage(err, 'Import failed'));
      setPhase('ready');
      setProgress(0);
    }
  };

  const handleMappingChange = (next: Record<string, string>): void => {
    setMapping(next);
    setPreview(null);
    setResult(null);
    setError(null);
    setSkipInvalidRows(false);
    if (phase === 'ready' || phase === 'done') {
      setPhase('mapping');
    }
  };

  const handleFilePicked = (next: File | null): void => {
    setFile(next);
    setPreview(null);
    setResult(null);
    setInspect(null);
    setMapping({});
    setError(null);
    setProgress(0);
    setRowEstimate(null);
    setConfirmOpen(false);
    setSkipInvalidRows(false);
    if (!next) {
      setPhase('idle');
      return;
    }
    void estimateCsvRowCount(next).then((count) => {
      if (count != null) setRowEstimate(count);
    });
    if (mappingEnabled) {
      void runInspect(next);
      return;
    }
    void runDryRun(next);
  };

  const openFilePicker = (): void => {
    if (busy) return;
    fileInputRef.current?.click();
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    if (busy) return;
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    if (busy) return;
    const next = event.dataTransfer.files?.[0] ?? null;
    if (!next || !isImportFile(next)) {
      setError(labels.invalidFile || labels.noFile);
      return;
    }
    handleFilePicked(next);
  };

  const validRowCount = preview ? preview.toCreate + preview.toUpdate : 0;
  const skippingInvalidRows =
    allowSkipInvalidRows && skipInvalidRows && Boolean(preview && preview.errors.length > 0);
  const canCommit =
    Boolean(file) &&
    phase === 'ready' &&
    mappingComplete &&
    Boolean(preview) &&
    preview!.dryRun === true &&
    preview!.total > 0 &&
    (preview!.errors.length === 0 || (skippingInvalidRows && validRowCount > 0));

  const canCheck =
    mappingEnabled &&
    Boolean(file) &&
    mappingComplete &&
    !busy &&
    (phase === 'mapping' || phase === 'ready');

  const handleCheckClick = (): void => {
    if (!file || !canCheck) return;
    void runDryRun(file);
  };

  const handleCommitClick = (): void => {
    if (!canCommit || !preview) return;
    if (skippingInvalidRows || preview.toUpdate > 0) {
      setConfirmOpen(true);
      return;
    }
    void runCommit();
  };

  const showProgress =
    phase === 'inspecting' || phase === 'previewing' || phase === 'importing';
  const rowCount = skippingInvalidRows
    ? validRowCount
    : preview?.total || result?.total || rowEstimate || 0;
  const progressLabel = (() => {
    if (phase === 'inspecting') {
      if (progress > 0 && progress < 100) {
        return labels.uploading || labels.inspecting || labels.checking;
      }
      return labels.inspecting || labels.checking;
    }
    if (phase === 'previewing') {
      if (progress > 0 && progress < 100) {
        return labels.uploading || labels.checking;
      }
      return rowCount > 0
        ? fill(labels.checkingWithCount || labels.checking, { count: rowCount })
        : labels.checking;
    }
    if (progress > 0 && progress < 100) {
      return labels.uploading || labels.importing;
    }
    return rowCount > 0
      ? fill(labels.importingWithCount || labels.importing, { count: rowCount })
      : labels.importing;
  })();

  const downloadErrorsCsv = (errors: ImportCsvErrorRow[]): void => {
    if (errors.length === 0) return;
    const lines = [
      [labels.row, labels.code, labels.message].map(csvEscape).join(','),
      ...errors.map((item) =>
        [item.row, item.code || '', resolveRowError(item)].map(csvEscape).join(',')
      ),
    ];
    const blob = new Blob([`\uFEFF${lines.join('\r\n')}\r\n`], {
      type: 'text/csv;charset=utf-8',
    });
    downloadBlob(blob, labels.errorsCsvFilename || 'import-errors.csv');
  };

  const renderStats = (stats: {
    total: number;
    createLabel: string;
    createValue: number;
    updateLabel: string;
    updateValue: number;
    errorCount?: number;
  }) => {
    const parts: Array<{ text: string; color: string }> = [
      { text: `${labels.total}: ${stats.total}`, color: 'text.primary' },
      { text: `${stats.createLabel}: ${stats.createValue}`, color: 'success.main' },
      { text: `${stats.updateLabel}: ${stats.updateValue}`, color: 'warning.main' },
    ];
    if ((stats.errorCount ?? 0) > 0) {
      parts.push({
        text: `${labels.errorsTitle}: ${stats.errorCount}`,
        color: 'error.main',
      });
    }
    return (
      <Typography variant="body2" component="div" sx={{ mt: 1 }}>
        {parts.map((part, index) => (
          <React.Fragment key={part.text}>
            {index > 0 && (
              <Typography variant="body2" component="span" color="text.disabled">
                {', '}
              </Typography>
            )}
            <Typography variant="body2" component="span" sx={{ color: part.color }}>
              {part.text}
            </Typography>
          </React.Fragment>
        ))}
      </Typography>
    );
  };

  const renderErrorTable = (errors: ImportCsvErrorRow[]) => (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <Button
          size="small"
          startIcon={<FileDownloadIcon fontSize="small" />}
          onClick={() => downloadErrorsCsv(errors)}
        >
          {labels.downloadErrors || labels.errorsTitle}
        </Button>
      </Box>
      <TableContainer sx={{ mt: 1, maxHeight: 240 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{labels.row}</TableCell>
              <TableCell>{labels.code}</TableCell>
              <TableCell>{labels.message}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {errors.slice(0, 100).map((item) => (
              <TableRow key={`${item.row}-${item.code}-${item.errorCode}-${item.message}`}>
                <TableCell>{item.row}</TableCell>
                <TableCell>{item.code || '—'}</TableCell>
                <TableCell>{resolveRowError(item)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth={mappingEnabled ? 'lg' : 'md'} fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {phase !== 'done' && (
            <>
              {templateHint &&
                (typeof templateHint === 'string' ? (
                  <Typography variant="body2" color="text.secondary">
                    {templateHint}
                  </Typography>
                ) : (
                  <Box sx={{ color: 'text.secondary', typography: 'body2' }}>
                    {templateHint}
                  </Box>
                ))}

              <input
                ref={fileInputRef}
                hidden
                type="file"
                accept={IMPORT_ACCEPT}
                onChange={(e) => {
                  const next = e.target.files?.[0] ?? null;
                  if (next && !isImportFile(next)) {
                    setError(labels.invalidFile || labels.noFile);
                    return;
                  }
                  handleFilePicked(next);
                }}
              />

              <Box
                role="button"
                tabIndex={busy ? -1 : 0}
                aria-label={file ? labels.changeFile : (labels.dropHint || labels.chooseFile)}
                aria-disabled={busy}
                onClick={openFilePicker}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openFilePicker();
                  }
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                  border: '2px dashed',
                  borderColor: isDragging ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  p: 2,
                  textAlign: 'center',
                  cursor: busy ? 'default' : 'pointer',
                  opacity: busy ? 0.6 : 1,
                  outline: 'none',
                  '&:hover': busy ? undefined : { borderColor: 'primary.main' },
                  '&:focus-visible': {
                    borderColor: 'primary.main',
                  },
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" gutterBottom>
                  {file ? file.name : (labels.dropHint || labels.chooseFile)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {file
                    ? `${formatFileSize(file.size)} · ${labels.changeFile}`
                    : (labels.acceptHint || labels.chooseFile)}
                </Typography>
              </Box>

              {mappingEnabled && inspect && columnFields && (
                <Box>
                  {labels.mappingTitle && (
                    <Typography variant="subtitle2" gutterBottom>
                      {labels.mappingTitle}
                    </Typography>
                  )}
                  {labels.mappingHint && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {labels.mappingHint}
                    </Typography>
                  )}
                  <ImportColumnMapTable
                    fields={columnFields}
                    headers={inspect.headers}
                    sampleRows={inspect.sampleRows}
                    mapping={mapping}
                    onChange={handleMappingChange}
                    disabled={busy}
                    labels={{
                      field: labels.mappingField || labels.code,
                      source: labels.mappingSource || labels.chooseFile,
                      sample: labels.mappingSample || labels.message,
                      skip: labels.mappingSkip || labels.cancel,
                    }}
                  />
                  {subjectFields.length > 0 && !subjectMapped ? (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      {labels.mappingSubjectHint || labels.mappingIncomplete}
                    </Alert>
                  ) : !mappingComplete && labels.mappingIncomplete ? (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      {labels.mappingIncomplete}
                    </Alert>
                  ) : null}
                </Box>
              )}

              {showProgress && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                    {progressLabel}
                    {progress > 0 ? ` · ${progress}%` : ''}
                  </Typography>
                  <LinearProgress
                    variant={progress > 0 ? 'determinate' : 'indeterminate'}
                    value={progress}
                  />
                </Box>
              )}

              {error && <Alert severity="error">{error}</Alert>}

              {preview && phase === 'ready' && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {labels.previewTitle}
                  </Typography>
                  {renderStats({
                    total: preview.total,
                    createLabel: labels.toCreate,
                    createValue: preview.toCreate,
                    updateLabel: labels.toUpdate,
                    updateValue: preview.toUpdate,
                    errorCount: preview.errors.length,
                  })}
                  {preview.errors.length === 0 ? (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      {labels.readyToCommit}
                    </Alert>
                  ) : (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      {skippingInvalidRows && labels.skipSummary
                        ? fill(labels.skipSummary, {
                            errors: preview.errors.length,
                            importCount: validRowCount,
                          })
                        : labels.hasErrors}
                    </Alert>
                  )}
                  {allowSkipInvalidRows && preview.errors.length > 0 && labels.skipInvalidRows && (
                    <FormControlLabel
                      sx={{ mt: 1, alignItems: 'center' }}
                      control={
                        <Checkbox
                          sx={{ p: 0, mr: 1 }}
                          checked={skipInvalidRows}
                          onChange={(event) => setSkipInvalidRows(event.target.checked)}
                        />
                      }
                      slotProps={{ typography: { variant: 'body2' } }}
                      label={labels.skipInvalidRows}
                    />
                  )}
                  {preview.errors.length > 0 && renderErrorTable(preview.errors)}
                </Box>
              )}
            </>
          )}

          {phase === 'done' && result && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="subtitle2">{labels.resultTitle}</Typography>
              {result.errors.length === 0 ? (
                renderStats({
                  total: result.total,
                  createLabel: labels.created,
                  createValue: result.created ?? 0,
                  updateLabel: labels.updated,
                  updateValue: result.updated ?? 0,
                })
              ) : (
                <>
                  {renderStats({
                    total: result.total,
                    createLabel: labels.created,
                    createValue: result.created ?? 0,
                    updateLabel: labels.updated,
                    updateValue: result.updated ?? 0,
                    errorCount: result.errors.length,
                  })}
                  <Alert
                    severity={
                      (result.created ?? 0) + (result.updated ?? 0) > 0 ? 'warning' : 'error'
                    }
                  >
                    {(result.created ?? 0) + (result.updated ?? 0) > 0 && labels.partialResult
                      ? labels.partialResult
                      : labels.hasErrors}
                  </Alert>
                  {renderErrorTable(result.errors)}
                </>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {phase !== 'done' && onDownloadTemplate && (
            <Button
              startIcon={<DescriptionIcon fontSize="small" />}
              onClick={() => void onDownloadTemplate()}
              disabled={busy}
              sx={{ mr: 'auto' }}
            >
              {labels.downloadTemplate}
            </Button>
          )}
          {phase === 'done' ? (
            <Button variant="contained" onClick={handleClose}>
              {labels.close}
            </Button>
          ) : (
            <>
              <Button onClick={handleClose} disabled={busy}>
                {labels.cancel}
              </Button>
              {mappingEnabled && (
                <Button
                  variant={phase === 'ready' ? 'outlined' : 'contained'}
                  onClick={handleCheckClick}
                  disabled={!canCheck}
                >
                  {labels.check || labels.checking}
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleCommitClick}
                disabled={!canCommit}
              >
                {labels.commit}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        title={skippingInvalidRows ? labels.confirmSkipTitle : labels.confirmUpdateTitle}
        message={
          skippingInvalidRows
            ? fill(labels.confirmSkipMessage || labels.readyToCommit, {
                errors: preview?.errors.length ?? 0,
                create: preview?.toCreate ?? 0,
                update: preview?.toUpdate ?? 0,
              })
            : fill(labels.confirmUpdateMessage || labels.readyToCommit, {
                count: preview?.toUpdate ?? 0,
              })
        }
        confirmLabel={
          (skippingInvalidRows ? labels.confirmSkipConfirm : labels.confirmUpdateConfirm) ||
          labels.commit
        }
        cancelLabel={labels.cancel}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          void runCommit();
        }}
      />
    </>
  );
};

export default ImportCsvDialog;
